import { Injectable } from "@angular/core";
import { Http } from "@angular/http";
import { Router } from "@angular/router";

import { BaseService } from "./base.service";
import { User } from "../models/user.model";
import { Role } from "../models/role.enum";
import { Subject } from "rxjs/Subject";
import { Permissions, UserPermission } from "../models/user-permission.model";

@Injectable()
export class AuthService extends BaseService {
  private _permissions: UserPermissions;
  private _role: Role;
  protected _permissions$: Subject<UserPermissions>;
  private _userPermissions: UserPermission[];

  constructor(protected http: Http, private router: Router) {
    super(http);
    this.init();
  }

  async init() {
    this._permissions$ = new Subject();
    try {
      this._role = await this.get("userRole");
      this._userPermissions = await this.getOwnPermissions();
      this.setupPermissions();
    } catch {}
  }

  async login(user: User) {
    return this.post("login", user, true);
  }

  async forgotPassword(email: string) {
    return this.post("forgotPassword", { email: email }, true);
  }

  async resetPassword(password: string, resetToken: string) {
    return this.post(
      "resetPassword",
      { password: password },
      false,
      resetToken
    );
  }

  async establishNewPassword(password: string, token: string) {
    return this.post(
      "establishNewPassword",
      { password: password },
      false,
      token
    );
  }

  async regsiter(newUser: {
    name: string;
    role: string;
    email: string;
    password?: string;
    siteId?: string;
    permissions?: UserPermission[];
  }): Promise<null> {
    return this.post("register", newUser);
  }

  async getRole(): Promise<Role> {
    return this.get("userRole");
  }

  async getStartingRoute(): Promise<string> {
    return this.get("user/startRoute");
  }

  async getUserPermissions(userId: number): Promise<UserPermission[]> {
    return this.get("admin/user/permissions/" + userId);
  }

  async getOwnPermissions(): Promise<UserPermission[]> {
    return this.get("user/permissions");
  }

  private setupPermissions() {
    let permissions: UserPermissions = {
      viewPatients: false,
      viewPatientData: false,
      managePendingApprovals: false,
      uploadData: false,
      searchData: false,

      adminManageTags: false,
      adminManageUsers: false,
      adminDeleteImage: false,
      adminDeletePatient: false,

      mactelDiagnosis: false,
      grade: false,
      dataLock: false,
      srGrader: false,
      clinicianReview: false,
      downloadData: false,

      reportBuilder: false,
      adjudicate: false,
    };

    let permissionsAllowed: Array<any>;
    switch (this._role) {
      case Role.admin:
        permissionsAllowed = [
          "viewPatients",
          "viewPatientData",
          "managePendingApprovals",
          "uploadData",
          "downloadData",
          "adminManageTags",
          "adminManageUsers",
          "adminDeleteImage",
          "mactelDiagnosis",
          "grade",
          "dataLock",
          "reportBuilder",
          "adjudicate",
          "searchData",
          "adminDeletePatient",
        ];
        break;
      case Role.researcher:
        permissionsAllowed = ["viewPatients", "viewPatientData", "uploadData"];
        for (let i = 0; i < this._userPermissions.length; i++) {
          if (
            this._userPermissions[i].permissionType ===
            Permissions.allPermissionTypes.downloadData
          ) {
            permissionsAllowed = permissionsAllowed.concat("downloadData");
          }
          if (
            this._userPermissions[i].permissionType ===
            Permissions.allPermissionTypes.accessReportBuilder
          ) {
            permissionsAllowed = permissionsAllowed.concat("reportBuilder");
          }
        }
        break;
      case Role.qc:
        permissionsAllowed = [
          "viewPatients",
          "managePendingApprovals",
          "uploadData",
          "downloadData",
          "adjudicate",
        ];
        break;
      case Role.technician:
        permissionsAllowed = ["uploadData"];
        break;
      case Role.grader:
        permissionsAllowed = ["mactelDiagnosis", "grade"];

        for (let i = 0; i < this._userPermissions.length; i++) {
          if (
            this._userPermissions[i].permissionType ===
            Permissions.allPermissionTypes.downloadData
          ) {
            permissionsAllowed = permissionsAllowed.concat("downloadData");
          }
          if (
            this._userPermissions[i].permissionType ===
            Permissions.allPermissionTypes.accessImages
          ) {
            permissionsAllowed = permissionsAllowed.concat("accessImages");
          }
          if (
            this._userPermissions[i].permissionType ===
            Permissions.allPermissionTypes.searchData
          ) {
            permissionsAllowed = permissionsAllowed.concat("searchData");
          }
          if (
            this._userPermissions[i].permissionType ===
            Permissions.allPermissionTypes.viewPatients
          ) {
            permissionsAllowed = permissionsAllowed.concat("viewPatients");
          }
          if (
            this._userPermissions[i].permissionType ===
            Permissions.allPermissionTypes.viewPatientData
          ) {
            permissionsAllowed = permissionsAllowed.concat("viewPatientData");
          }
          if (
            this._userPermissions[i].permissionType ===
            Permissions.allPermissionTypes.managePendingApprovals
          ) {
            permissionsAllowed = permissionsAllowed.concat(
              "managePendingApprovals"
            );
          }
          if (
            this._userPermissions[i].permissionType ===
            Permissions.allPermissionTypes.uploadData
          ) {
            permissionsAllowed = permissionsAllowed.concat("uploadData");
          }
          if (
            this._userPermissions[i].permissionType ===
            Permissions.allPermissionTypes.dataLock
          ) {
            permissionsAllowed = permissionsAllowed.concat("dataLock");
          }
          if (
            this._userPermissions[i].permissionType ===
            Permissions.allPermissionTypes.srGrader
          ) {
            permissionsAllowed = permissionsAllowed.concat("srGrader");
          }
          if (
            this._userPermissions[i].permissionType ===
            Permissions.allPermissionTypes.clinicianReview
          ) {
            permissionsAllowed = permissionsAllowed.concat("clinicianReview");
          }
          if (
            this._userPermissions[i].permissionType ===
            Permissions.allPermissionTypes.adjudicate
          ) {
            permissionsAllowed = permissionsAllowed.concat("adjudicate");
          }
          if (
            this._userPermissions[i].permissionType ===
            Permissions.allPermissionTypes.accessReportBuilder
          ) {
            permissionsAllowed = permissionsAllowed.concat("reportBuilder");
          }
        }
        break;
    }
    permissionsAllowed.forEach((p) => {
      permissions[p] = true;
    });
    this._permissions$.next(permissions);
  }

  get permissions$(): Subject<UserPermissions> {
    return this._permissions$;
  }

  logout() {
    localStorage.removeItem("Authorization");
    localStorage.removeItem("LocalSearch");
    sessionStorage.removeItem("Authorization");

    this.router.navigate(["/login"]);
  }
}

export interface UserPermissions {
  viewPatients: boolean;
  viewPatientData: boolean; // if viewPatients and !viewPatient data, just can't view Emmes data
  managePendingApprovals: boolean;
  uploadData: boolean;
  downloadData: boolean;
  searchData: boolean;

  adminManageTags: boolean;
  adminManageUsers: boolean;
  adminDeleteImage: boolean;
  adminDeletePatient: boolean;

  mactelDiagnosis: boolean;
  grade: boolean;
  srGrader: boolean;
  clinicianReview: boolean;
  dataLock: boolean;

  reportBuilder: boolean;
  adjudicate: boolean;
}



// WEBPACK FOOTER //
// ./src/app/services/auth.service.ts